import React, { useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import { Box, Checkbox, FormControlLabel, Grid, TextField, Divider } from '@mui/material';
import { LuiSelect, LuiSelectItemEvent } from '@jda/lui-common-component-library-mui5';
import { fetchUpdateFormValue } from '../../store/passwordPolicies/passwordPoliciesActionCreator';
import { selectFormValues } from '../../store/passwordPolicies/passwordPoliciesSelector';
import {
  CONSECUTIVE_LETTERS_DESCRIPTION,
  EXCLUSIONS_DESCRIPTION,
  EXPIRATION_DURATION_DESCRIPTION,
  INCLUSIONS_DESCRIPTION,
  LENGTH_DESCRIPTION,
  RESET_TO_DEFAULT,
  FORM_FIELDS,
  maxConsecutiveLettersOptions,
  PASSWORD_HISTORY_DESCRIPTION
} from './constants';
import { useStyles } from './styles';
import { getPasswordPolicyRule } from '../../services/setting';
import { DROPDOWN_MAX_HEIGHT } from "../../container/Users/constants";

export default function PasswordPolicyForm({ defaultPolicy }: any) {
  const selectedFormValues = useSelector(selectFormValues);
  const {
    policyName,
    minLength,
    maxLength,
    expiryDuration,
    maxConsecutiveLetters,
    includeNumbers,
    includeSpecialCharacters,
    lettersInMixedCase,
    excludeCommonUnsafePassword,
    excludeUserProfileInformation,
    passwordHistoryEnabled,
    oldPasswords,
  } = selectedFormValues;

  const maxConsecutiveLettersSelected = useMemo(
    () => maxConsecutiveLettersOptions.find(option => option.value === maxConsecutiveLetters.value),
    [maxConsecutiveLetters.value]
  );

  const dispatch = useDispatch();
  const { classes } = useStyles();

  const isNumeric = (value: any) => /^\d*$/.test(value);

  const handleChangeInput = (e: { target: { name: string; value: any } }) => {
    const { minLength, maxLength, expiryDuration } = FORM_FIELDS;
    const { name: fieldName, value } = e.target;
    let isValid = true;

    switch (fieldName) {
      case minLength:
        isValid = value.length <= 8 && isNumeric(value);
        break;
      case maxLength:
        isValid = value.length <= 22 && isNumeric(value);
        break;
      case expiryDuration:
        isValid = isNumeric(value);
        break;
    }

    if (isValid) {
      dispatch(fetchUpdateFormValue({ [fieldName]: { value: value, error: false, errorHelperText: '' } }));
    }
  };

  const handleChangeMaxOldPasswordInput = (e: { target: { name: string; value: any } }) => {
    const { name: fieldName, value } = e.target;
    let isValid = false;

    if (isNumeric(value)) {
      isValid = isNumeric(value);
    }
    if (isValid) {
      dispatch(fetchUpdateFormValue({ [fieldName]: { value: value, error: false, errorHelperText: '' } }));
    }
  };

  const handleDropdownChange = (event: LuiSelectItemEvent) => {
    const selectedValue: any = event.target.value;
    const { maxConsecutiveLetters } = FORM_FIELDS;
    dispatch(
      fetchUpdateFormValue({
        [maxConsecutiveLetters]: { value: selectedValue.value, error: false, errorHelperText: '' }
      })
    );
  };

  const handleChangeCheckBox = (e: React.ChangeEvent<HTMLInputElement>) => {
    dispatch(fetchUpdateFormValue({ [e.target.name]: e.target.checked }));
    if (e.target.name === 'passwordHistoryEnabled' && e.target.checked === false) {
      dispatch(fetchUpdateFormValue({ oldPasswords: { value: '0', error: false, errorHelperText: '' } }));
    }
  };

  const handleButtonReset = (event: any) => {
    const fields = event.currentTarget.getAttribute('data-fields').split(',');
    getPasswordPolicyRule(defaultPolicy.id).then(res => {
      const checkboxFields = [
        FORM_FIELDS.includeNumbers,
        FORM_FIELDS.includeSpecialCharacters,
        FORM_FIELDS.lettersInMixedCase,
        FORM_FIELDS.excludeCommonUnsafePassword,
        FORM_FIELDS.excludeUserProfileInformation,
        FORM_FIELDS.passwordHistoryEnabled
      ];

      fields.forEach((field: any) => {
        if (checkboxFields.includes(field)) {
          dispatch(fetchUpdateFormValue({ [field]: res[field] }));
        } else {
          dispatch(
            fetchUpdateFormValue({
              [field]: { value: res[field], error: false, errorHelperText: '' }
            })
          );
        }
      });
    });
  };

  const getSectionTitle = (title: string, description: string) => {
    return (
      <Box>
        <Typography variant="body2" className={classes.sectionTitle}>
          {title}
        </Typography>
        <Typography variant="body2" className={classes.sectionDescription}>
          {description}
        </Typography>
      </Box>
    );
  };

  return (
    <Box>
      <Grid container>
        <Grid item xs={12}>
          <TextField
            value={policyName.value || ''}
            onChange={handleChangeInput}
            variant="standard"
            name="policyName"
            type="text"
            label="Policy Name"
            error={policyName.error}
            helperText={policyName.errorHelperText}
            style={{ marginBottom: '10px', width: '50%' }}
            data-testid="policyName"
            size='small'
          />
        </Grid>
      </Grid>

      {getSectionTitle('Length', LENGTH_DESCRIPTION)}
      <Grid container spacing={2} sx={{ alignItems: 'center', marginTop: '5px' }}>
        <Grid item xs={4}>
          <TextField
            variant="standard"
            value={minLength.value || ''}
            onChange={handleChangeInput}
            name={FORM_FIELDS.minLength}
            type="text"
            label="Minimum Characters"
            placeholder={'8 characters'}
            inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
            error={minLength.error}
            helperText={minLength.errorHelperText}
            data-testid="minLength"
            size='small'
          />
        </Grid>
        <Grid item xs={4}>
          <TextField
            value={maxLength.value || ''}
            onChange={handleChangeInput}
            name={FORM_FIELDS.maxLength}
            variant="standard"
            data-testid="maxLength"
            type="text"
            label="Maximum Characters"
            inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
            placeholder={'22 characters'}
            fullWidth
            error={maxLength.error}
            helperText={maxLength.errorHelperText}
            size='small'
          />
        </Grid>
        <Grid item xs={4} className={classes.buttonResetLength}>
          <Button
            variant="outlined"
            color="primary"
            size='small'
            className={classes.buttonReset}
            data-testid="buttonResetLength"
            data-type="buttonResetLength"
            data-fields={`${FORM_FIELDS.minLength},${FORM_FIELDS.maxLength}`}
            onClick={handleButtonReset}
          >
            {RESET_TO_DEFAULT}
          </Button>
        </Grid>
      </Grid>

      <Divider classes={{ root: classes.divider }} />

      {getSectionTitle('Expiration Duration', EXPIRATION_DURATION_DESCRIPTION)}
      <Grid container spacing={2} sx={{ alignItems: 'center', marginTop: '5px' }}>
        <Grid item xs={4} className={''}>
          <TextField
            value={expiryDuration.value || ''}
            onChange={handleChangeInput}
            name="expiryDuration"
            type="text"
            variant="standard"
            size='small'
            placeholder={'0 day'}
            error={expiryDuration.error}
            helperText={expiryDuration.errorHelperText}
            data-testid="expiryDuration"
          />
        </Grid>
        <Grid item xs={4} className={''}>
          <Button
            variant="outlined"
            color="primary"
            size='small'
            className={classes.buttonReset}
            data-testid="buttonResetExpirationDuration"
            data-type="buttonResetExpirationDuration"
            data-fields={FORM_FIELDS.expiryDuration}
            onClick={handleButtonReset}
          >
            {RESET_TO_DEFAULT}
          </Button>
        </Grid>
      </Grid>

      <Divider classes={{ root: classes.divider }} />

      <Grid container style={{ display: 'flex', alignItems: 'center' }}>
        <Typography variant="body2" style={{ marginRight: '30px', fontWeight: 500 }}>
          Inclusions and Exclusions
        </Typography>
        <Button
          variant="outlined"
          color="primary"
          size="small"
          className={classes.buttonReset}
          data-testid="buttonResetInclusionsExclusions"
          data-type="buttonResetInclusionsExclusions"
          data-fields="includeNumbers,includeSpecialCharacters,lettersInMixedCase,excludeCommonUnsafePassword,excludeUserProfileInformation"
          onClick={handleButtonReset}
        >
          {RESET_TO_DEFAULT}
        </Button>
      </Grid>
      <Typography variant="body1" style={{ marginTop: '5px', marginBottom: '5px', fontSize: '12px' }}>
        {INCLUSIONS_DESCRIPTION}
      </Typography>

      <Grid container>
        <Grid item xs={4} className={''}>
          <FormControlLabel
            control={
              <Checkbox
                checked={includeNumbers || false}
                color="primary"
                name="includeNumbers"
                onChange={handleChangeCheckBox}
                data-testid="includeNumbers"
                size='small'
              />
            }
            label={
              <Box>
                <span className={classes.checkBoxLabel}>Include numbers</span>
                <span className={classes.checkBoxLabelDescription}>(e.g. 1, 2, 3, 4, etc)</span>
              </Box>
            }
            labelPlacement="end"
          />
        </Grid>
        <Grid item xs={4} className={''}>
          <FormControlLabel
            control={
              <Checkbox
                checked={includeSpecialCharacters || false}
                color="primary"
                name="includeSpecialCharacters"
                onChange={handleChangeCheckBox}
                data-testid="includeSpecialCharacters"
                size='small'
              />
            }
            label={
              <Box>
                <span className={classes.checkBoxLabel}>Include special characters</span>
                <span className={classes.checkBoxLabelDescription}>(e.g. !, @, #, $, etc)</span>
              </Box>
            }
            labelPlacement="end"
          />
        </Grid>
        <Grid item xs={4} className={''}>
          <FormControlLabel
            control={
              <Checkbox
                checked={lettersInMixedCase || false}
                color="primary"
                name="lettersInMixedCase"
                onChange={handleChangeCheckBox}
                data-testid="lettersInMixedCase"
                size='small'
              />
            }
            label={
              <Box>
                <span className={classes.checkBoxLabel}>Include letters in mixed case</span>
                <span className={classes.checkBoxLabelDescription}>(e.g. a, A, b, B, etc)</span>
              </Box>
            }
            labelPlacement="end"
          />
        </Grid>
      </Grid>
      <Typography style={{ marginTop: '5px', marginBottom: '5px', fontSize: '12px' }} variant="body1">
        {EXCLUSIONS_DESCRIPTION}
      </Typography>

      <Grid container>
        <Grid item xs={4} className={''}>
          <FormControlLabel
            control={
              <Checkbox
                checked={excludeCommonUnsafePassword || false}
                color="primary"
                name="excludeCommonUnsafePassword"
                onChange={handleChangeCheckBox}
                data-testid="excludeCommonUnsafePassword"
                size='small'
              />
            }
            label={
              <Box>
                <span className={classes.checkBoxLabel}>Exclude common unsafe passwords</span>
                <span className={classes.checkBoxLabelDescription}>(e.g. 123456, qwuerty, etc)</span>
              </Box>
            }
            labelPlacement="end"
          />
        </Grid>
        <Grid item xs={4} className={''}>
          <FormControlLabel
            control={
              <Checkbox
                checked={excludeUserProfileInformation || false}
                color="primary"
                name="excludeUserProfileInformation"
                onChange={handleChangeCheckBox}
                data-testid="excludeUserProfileInformation"
                size='small'
              />
            }
            label={
              <Box>
                <span className={classes.checkBoxLabel}>Exclude user profile information</span>
                <span className={classes.checkBoxLabelDescription}>(e.g username, email, first name, etc)</span>
              </Box>
            }
            labelPlacement="end"
          />
        </Grid>
      </Grid>

      <Divider classes={{ root: classes.divider }} />

      {getSectionTitle('Consecutive Letters', CONSECUTIVE_LETTERS_DESCRIPTION)}
      <Grid container spacing={2} sx={{ alignItems: 'center', marginTop: '5px' }}>
        <Grid item xs={4}>
          <LuiSelect
            selectedItem={maxConsecutiveLettersSelected}
            itemData={maxConsecutiveLettersOptions}
            onSelectionChange={handleDropdownChange}
            inputLabel={''}
            maxHeight={DROPDOWN_MAX_HEIGHT}
            error={maxConsecutiveLetters.error}
            helperText={maxConsecutiveLetters.errorHelperText}
            size='small'
          />
        </Grid>
        <Grid item xs={4}>
          <Button
            variant="outlined"
            color="primary"
            className={`${classes.buttonReset} ${classes.buttonResetConsecutiveLetters}`}
            data-testid="buttonResetConsecutiveLetters"
            data-type="buttonResetConsecutiveLetters"
            data-fields={FORM_FIELDS.maxConsecutiveLetters}
            onClick={handleButtonReset}
            size='small'
          >
            {RESET_TO_DEFAULT}
          </Button>
        </Grid>
      </Grid>

      {getSectionTitle('Password History', PASSWORD_HISTORY_DESCRIPTION)}
      <Grid container spacing={2} sx={{ alignItems: 'flex-end', marginTop: '5px' }}>
        <Grid item xs={4} className={''}>
          <FormControlLabel
            control={
              <Checkbox
                color="primary"
                name="passwordHistoryEnabled"
                onChange={handleChangeCheckBox}
                checked={passwordHistoryEnabled}
                data-testid="passwordHistoryEnabled"
                size='small'
              />
            }
            label={
              <Box>
                <span className={classes.checkBoxLabel}>Password History</span>
              </Box>
            }
            labelPlacement="end"
          />
        </Grid>
        {passwordHistoryEnabled &&
          <Grid item xs={4} className={''}>
            <TextField
              variant="standard"
              label={'Last Passwords'}
              value={oldPasswords?.value || ''}
              onChange={handleChangeMaxOldPasswordInput}
              name={'oldPasswords'}
              type="text"
              inputProps={{ inputMode: 'numeric', pattern: '[0-9]*', maxLength: 3 }}
              placeholder={'24'}
              fullWidth
              error={oldPasswords?.error}
              helperText={oldPasswords?.errorHelperText}
              data-testid="oldPasswords"
              size='small'
            />
          </Grid>
        }
        <Grid item xs={4}>
          <Button
            variant="outlined"
            color="primary"
            size='small'
            onClick={handleButtonReset}
            data-testid="buttonResetPasswordHistory"
            data-type="buttonResetPasswordHistory"
            className={`${classes.buttonReset} ${classes.buttonResetConsecutiveLetters}`}
            data-fields={`${FORM_FIELDS.passwordHistoryEnabled},${FORM_FIELDS.oldPasswords}`}
          >
            {RESET_TO_DEFAULT}
          </Button>
        </Grid>
      </Grid>
    </Box>
  );
}